<template>
  <am-form-layout
      title="Union Details"
      submit-text="Save"
      :show-loading="updatingForm"
      :show-reset="false"
      submit-button-name="update-school"
      @onSubmit="updateCompany()"
  >
    <!-- Logo Section -->
    <template slot="form-header">
      <div class="d-flex justify-content-center mb-2">
        <div class="d-flex flex-column justify-content-start">
          <div class="text-center mb-2">
            <am-profile-photo
                v-show="!updatingLogo && logoUrl"
                size="80"
                :photo="logoUrl"
            />
            <b-avatar
                v-show="!updatingLogo && !logoUrl"
                size="80px"
                variant="light-primary"
            />
            <b-spinner
                v-show="updatingLogo"
                style="width: 80px; height: 80px;"
                variant="primary"
            />
          </div>
          <am-file-button
              label="Upload Logo"
              :loading="updatingLogo"
              @onFileUpload="uploadLogo"
          />
        </div>
      </div>
    </template>

    <template slot="form-elements">
      <!-- General Information Section -->
      <div class="col-12">
        <h4 class="mb-2 mt-2">General Information</h4>
        <hr class="mt-0">
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.name"
            name="name"
            label="Name"
            rules="required"
        />
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.registration_no"
            name="registration_no"
            label="Registration Number"
            rules="required"
        />
      </div>
      <div class="col-md-4">
        <am-date-picker
            v-model="company.date_established"
            name="date_established"
            label="Date Established"
            rules="required|no_future_date"
        />
      </div>
      <div class="col-md-4">
        <am-date-picker
            v-model="company.date_registered"
            name="date_registered"
            label="Date Registered"
            rules="no_future_date"
        />
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.vat"
            name="vat"
            label="Vat"
            :rules="''"
        />
      </div>
      <div class="col-md-4">
        <am-select
            v-model="company.company_classification_id"
            :options="classifications"
            name="classification"
            label-name="name"
            value-name="id"
            label="Classification"
            rules="required"
        />
      </div>
      <!-- Sustainability Certification Dropdown -->
      <div class="col-md-4">
        <am-select
            v-model="company.sustainability_certification"
            :options="sustainabilityCertificationOptions"
            name="sustainability_certification"
            label="Sustainability Certification"
            :rules="''"
            placeholder="Select Certification"
        />
      </div>
      <!-- Conditional Input for 'Other' Certification -->
      <div class="col-md-4" v-if="showOtherSustainabilityInput">
        <am-input
            v-model="company.other_sustainability_certification"
            name="other_sustainability_certification"
            label="Specify Other Certification"
            rules="required"
        />
      </div>
      <!-- Partner Organization -->
      <div class="col-md-4">
        <am-input
            v-model="company.partner_organization"
            name="partner_organization"
            label="Partner Organizations"
            placeholder="Enter Partner Organizations(Separate with Comma)"
            :rules="''"
        />
      </div>

      <!-- Address Information Section -->
      <div class="col-12">
        <h4 class="mb-2 mt-3">Address Information</h4>
        <hr class="mt-0">
      </div>
      <div class="col-md-4">
        <am-select
            v-model="company.country"
            :options="countries"
            name="country"
            label="Country"
            rules="required"
            label-name="name"
            value-name="id"
            placeholder="Select Country"
        />
      </div>
      <div class="col-md-4">
        <am-select
            v-model="company.region"
            :options="regions"
            name="region"
            label="Region"
            rules="required"
            label-name="name"
            value-name="id"
            :disabled="!company.country"
            placeholder="Select Region"
        />
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.district"
            name="district"
            label="District"
            rules="required"
        />
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.address"
            name="address"
            label="Address"
            rules="required"
        />
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.location"
            name="location"
            label="Location"
            rules="required"
        />
      </div>

      <!-- Contact Information Section -->
      <div class="col-12">
        <h4 class="mb-2 mt-3">Contact Information</h4>
        <hr class="mt-0">
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.contact_name"
            name="contact_name"
            label="Contact Name"
            rules="required"
        />
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.contact_phone"
            name="contact_phone"
            label="Contact Phone"
            rules="required"
            type="number"
        />
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.secondary_contact"
            name="secondary_contact"
            label="Secondary Contact"
            rules="required"
        />
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.secondary_contact_phone"
            name="secondary_contact_phone"
            label="Secondary Contact Phone No."
            rules="required"
            type="number"
        />
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.email"
            name="email"
            label="Email"
            rules="email"
        />
      </div>

      <!-- Financial Details Section -->
      <div class="col-12">
        <h4 class="mb-2 mt-3">Financial Details</h4>
        <hr class="mt-0">
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.bank_name"
            name="bank_name"
            label="Bank Name"
            :rules="''"
        />
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.bank_account_no"
            name="bank_account_no"
            label="Bank Account No."
            :rules="''"
        />
      </div>
      <div class="col-md-4">
        <am-input
            v-model="company.branch_name"
            name="branch_name"
            label="Branch Name"
            :rules="''"
        />
      </div>
      <div class="col-md-4">
        <am-select
            v-model="company.funding_source"
            :options="fundingSourceOptions"
            name="funding_source"
            label="Funding Source"
            :rules="''"
            placeholder="Select Funding Source"
        />
      </div>
      <div class="col-md-4">
        <am-select
            v-model="company.annual_revenue"
            :options="annualRevenueOptions"
            name="annual_revenue"
            label="Annual Revenue"
            rules="required"
            placeholder="Select Annual Revenue"
        />
      </div>
      <div class="col-md-4">
        <am-select
            v-model="company.by_laws"
            :options="byLawsOptions"
            name="by_laws"
            label="By-Laws"
            rules="required"
            label-name="text"
            value-name="value"
            placeholder="Select"
        />
      </div>

      <!-- Notes Section -->
      <div class="col-12">
        <h4 class="mb-2 mt-3">Notes</h4>
        <hr class="mt-0">
      </div>
      <div class="col-12">
        <am-text-area
            v-model="company.notes"
            name="notes"
            placeholder="Enter any relevant notes here..."
            label="Notes"
            type="textarea"
            rows="3"
            :rules="''"
        />
      </div>

      <!-- Error Display -->
      <div class="col-12 mt-2">
        <error-display :error="error" />
      </div>
    </template>
  </am-form-layout>
</template>

<script>
import { BAvatar, BSpinner } from 'bootstrap-vue'
import AmFormLayout from '@/farmbusiness/components/layouts/AmFormLayout'
import AmProfilePhoto from '@/farmbusiness/components/AmProfilePhoto'
import AmFileButton from '@/farmbusiness/components/AmFileButton'
import AmInput from '@/farmbusiness/components/AmInput'
import ErrorDisplay from '@/farmbusiness/components/AmErrorDisplay'
import AmSelect from '@/farmbusiness/components/AmSelect'
import showToast from '@/farmbusiness/mixins/showToast'
import AmDatePicker from "@/farmbusiness/components/AmDatePicker";
import AmTextArea from "@/farmbusiness/components/AmTextArea";

// Placeholder for AmDatePicker if it's not a real component imported above
// const AmDatePicker = {
//   props: ['value', 'name', 'label', 'rules'],
//   template: `
//     <div>
//       <label :for="name">{{ label }} <span v-if="rules.includes('required')" class="text-danger">*</span></label>
//       <input
//         type="date" :id="name" :name="name" :value="value"
//         @input="$emit('input', $event.target.value)" class="form-control"
//       >
//     </div>`
// }

export default {
  name: 'Company',
  components: {
    AmTextArea,
    AmDatePicker,
    AmSelect,
    ErrorDisplay,
    AmInput,
    AmFileButton,
    AmProfilePhoto,
    AmFormLayout,
    BAvatar,
    BSpinner,
  },
  mixins: [showToast],
  data() {
    return {
      company: {
        // Existing fields
        name: null,
        address: null,
        postal_code: null,
        vat: null,
        registration_no: null,
        email: null,
        company_classification_id: null,
        date_established: null,
        date_registered: null,
        location: null,
        country: null, // Holds selected Country ID
        region: null,  // Holds selected Region ID
        district: null,
        contact_name: null,
        contact_phone: null,
        secondary_contact: null,
        secondary_contact_phone: null,
        sustainability_certification: null, // Holds selected value ('Other' or specific cert)
        other_sustainability_certification: null, // Holds the custom text input value
        bank_name: null,
        bank_account_no: null,
        branch_name: null,
        funding_source: null,
        annual_revenue: null,
        partner_organization: null,
        by_laws: false,
        notes: null,
      },
      updatingLogo: false,
      updatingForm: false,
      error: {}, // For displaying form errors

      // --- Dropdown Options ---
      sustainabilityCertificationOptions: [
        'Fairtrade',
        'ISO 22000 & HACCP',
        'Cocoa life',
        'Organic certificate',
        'Rainforest alliance (RA)',
        'Mondelez international',
        'GHAFFAP',
        'Other' // The value that triggers the conditional input
      ],
      fundingSourceOptions: [
        'Loans',
        'Grants',
        'Donations',
        'Member contributions',
        'Cooperative owned business'
      ],
      annualRevenueOptions: [
        '<10k',
        '>10k but <50k',
        '>50k <100k',
        '>100k'
      ],
      byLawsOptions: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      countries: [], // Populated by API
      // Regions are derived from selectedCountry computed property
    }
  },
  computed: {
    logoUrl() {
      // Assuming Vuex getter for logo URL
      return this.$store.getters['company/companyLogo'];
    },
    classifications() {
      // Assuming Vuex getter for classifications
      return this.$store.getters['company/classifications'] || [];
    },
    companyDetails() {
      // Assuming Vuex getter for the full company details object
      return this.$store.getters['company/company'];
    },
    selectedCountry(){
      // Find the selected country object based on the stored ID
      if (!this.company.country || !Array.isArray(this.countries)) return null;
      // Ensure comparison works (e.g., if one is number and other string)
      return this.countries.find((c)=> String(c.id) === String(this.company.country));
    },
    regions(){
      // Get regions from the selected country object, ensure it exists and has regions
      return this.selectedCountry?.regions || [];
    },
    // Computed property to control visibility of the 'Other' input field
    showOtherSustainabilityInput() {
      return this.company.sustainability_certification === 'Other';
    }
  },
  watch: {
    // Watcher to clear 'other' field when main selection changes from 'Other'
    'company.sustainability_certification'(newValue) {
      if (newValue !== 'Other') {
        this.company.other_sustainability_certification = null;
      }
    },
    // Watch country change to reset region
    'company.country'(newCountryId, oldCountryId) {
      // Reset region only if the country actually changed
      if (newCountryId !== oldCountryId) {
        this.company.region = null;
      }
      // Note: Fetching regions dynamically would happen here if not preloaded with countries
    }
  },
  mounted() {
    const details = this.companyDetails || {};
    this.company = { ...this.company, ...details };
    // Ensure boolean select matches Yes/No options (API/DB default is false)
    this.company.by_laws = details.by_laws === true || details.by_laws === 1 || details.by_laws === '1';
    setTimeout(()=>{
      this.company.region = details.region
    },500)
    this.fetchCountries();
  },
  methods: {
    fetchCountries(){
      this.$http.get('system/countries').then((res)=>{
        this.countries = res.data
      })
    },
    updateCompany() {
      this.updatingForm = true
      this.error = {}
      // Create a payload copy to avoid modifying the original object directly if needed
      const payload = { ...this.company };
      delete payload.logo // Ensure logo (handled separately) isn't sent with form data
      this.$store.dispatch('company/updateCompany', payload)
          .then(() => {
            this.showSuccess('Updated Successfully')
          })
          .catch(error => {
            this.error = error
            this.showError('Failed to update details')
          }).finally(() => {
        this.updatingForm = false
      })
    },
    uploadLogo(log) {
      const logo = new FormData()
      logo.append('logo', log.file)
      this.updatingLogo = true
      this.$store.dispatch('company/updateLogo', logo)
          .then(() => {
            this.showSuccess('Logo Updated Successfully')
          })
          .catch(() => {
            this.showError('Failed to update logo')
          }).finally(() => {
        this.updatingLogo = false
      })
    },
  },
}
</script>

<style scoped>
hr {
  border-top: 1px solid #dee2e6;
  margin-bottom: 1.5rem;
}
h4 {
  color: #6e6b7b;
  font-weight: 500;
}
</style>